import React, { useEffect, useRef, useState } from 'react';
import { computeBazi } from './engine.js';
import { SHENSHA_MEANING } from './meanings.js';
import PillarCard from './PillarCard.jsx';
import PillarDetail from './PillarDetail.jsx';
import StrengthPanel from './StrengthPanel.jsx';
import DayunStrip from './DayunStrip.jsx';
import RelationsPanel from './RelationsPanel.jsx';
import SavedList from './SavedList.jsx';
import { listSaved, saveBazi } from './storage.js';
import { CITIES, trueSolar } from './solarTime.js';
import { smoothScrollToElement } from '../../utils/smoothScroll';
// [MHAI] MeiHua 集成：八字AI求测面板（上游同步时以下三个标注块可整体剔除）
import BaziAiChat from '../../components/BaziAiChat';

const WX_COLOR = { 木: '#2e7d32', 火: '#c62828', 土: '#9c6b3f', 金: '#607d8b', 水: '#1565c0' };
const WX_ORDER = ['木', '火', '土', '金', '水'];

function nowForm() {
  const d = new Date();
  return {
    year: d.getFullYear() - 30, month: d.getMonth() + 1, day: d.getDate(),
    hour: d.getHours(), minute: d.getMinutes(), gender: '男', name: '',
  };
}

export default function BaziPage() {
  const [form, setForm] = useState(nowForm);
  const [bazi, setBazi] = useState(null);
  const [error, setError] = useState('');
  const [savedList, setSavedList] = useState(listSaved);
  const [openPillar, setOpenPillar] = useState(null); // 当前展开详解的柱
  const [city, setCity] = useState('不校正');           // 真太阳时校正城市

  // 排盘：选了城市则先做真太阳时校正（专业排盘标配），钟表时间仅作展示
  const compute = (f = form, c = city) => {
    try {
      setError('');
      const ts = trueSolar({ year: f.year, month: f.month, day: f.day, hour: f.hour, minute: f.minute || 0, city: c });
      const pad = (n) => String(n).padStart(2, '0');
      const clockText = ts ? `${pad(f.hour)}:${pad(f.minute || 0)}` : '';
      const b = computeBazi({
        ...f,
        hour: ts ? ts.hour : f.hour,
        minute: ts ? ts.minute : (f.minute || 0),
        city: c,
        clockText,
      });
      if (ts) {
        b.trueSolarInfo = ts;
        b.clockTime = clockText;
        b.clockHour = f.hour;
        b.clockMinute = f.minute || 0;
      }
      setBazi(b);
      return b;
    } catch (err) {
      setError(err.message || String(err));
      return null;
    }
  };
  useEffect(() => { compute(); }, []);

  const change = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: name === 'gender' || name === 'name' ? value : (value === '' ? '' : parseInt(value, 10)) });
  };

  // 从存档载入（含分钟）
  const loadFromSaved = (rec) => {
    if (!rec) return;
    const m = /(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})/.exec(rec.solarText);
    if (!m) return;
    const f = { year: +m[1], month: +m[2], day: +m[3], hour: +m[4], minute: +m[5] || 0, gender: rec.gender || '男', name: rec.name };
    setForm(f);
    setCity(rec.city || '不校正');
    compute(f, rec.city || '不校正');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 输入是否已偏离当前盘面（改了没点「排盘」）
  const cmpHour = bazi ? (bazi.trueSolarInfo ? bazi.clockHour : bazi.input.hour) : 0;
  const cmpMin = bazi ? (bazi.trueSolarInfo ? bazi.clockMinute : bazi.input.minute || 0) : 0;
  const formDirty = !!bazi && (
    form.year !== bazi.input.year || form.month !== bazi.input.month ||
    form.day !== bazi.input.day || form.hour !== cmpHour ||
    (form.minute || 0) !== cmpMin ||
    form.gender !== bazi.input.gender || (form.name || '') !== (bazi.input.name || '') ||
    city !== (bazi.input.city || '不校正')
  );

  // [MHAI] 恢复八字AI历史会话：历史页「去八字页继续」会把盘面快照写入
  // localStorage['bz-ai-restore']，这里消费一次并按存盘口径重排当前盘。
  const mhRestoreOnce = useRef(false);
  useEffect(() => {
    if (mhRestoreOnce.current) return;
    mhRestoreOnce.current = true;
    try {
      const raw = localStorage.getItem('bz-ai-restore');
      if (!raw) return;
      localStorage.removeItem('bz-ai-restore');
      const snap = JSON.parse(raw);
      const chart0 = snap && snap.chart;
      if (!chart0 || !Array.isArray(chart0.pillars) || !chart0.input) return;
      const inp = chart0.input;
      const clkH = chart0.trueSolarInfo ? (chart0.clockHour ?? inp.hour) : inp.hour;
      const clkM = chart0.trueSolarInfo ? (chart0.clockMinute ?? (inp.minute || 0)) : (inp.minute || 0);
      const f = {
        year: inp.year, month: inp.month, day: inp.day,
        hour: clkH, minute: clkM, gender: inp.gender || '男', name: inp.name || '',
      };
      const c = inp.city || '不校正';
      setForm(f);
      setCity(c);
      compute(f, c);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch { /* 忽略损坏的历史快照 */ }
  }, []);
  /* [MHAI] 恢复逻辑结束 */

  const doSave = () => {
    if (!bazi) return;
    // 关键修复：按「当前输入」重新排盘后再存，杜绝存到旧盘
    const fresh = compute({ ...form }, city) || bazi;
    setSavedList(saveBazi(fresh));
  };

  const totalWx = bazi ? WX_ORDER.reduce((s, w) => s + bazi.wuxingCount[w], 0) || 1 : 1;

  return (
    <div className="app">
      <header className="app-header">
        <div className="page-eyebrow">八字命理 · 四柱十神</div>
        <h1>八字排盘</h1>
        <p className="subtitle">四柱 · 十神 · 藏干纳音长生 · 柱际生克 · 神煞 · 大运流年</p>
        {/* [MHAI] 八字页 AI 批命入口（上游同步时可整体剔除） */}
        {bazi && (
          <button
            type="button"
            className="ai-jump-btn"
            onClick={() => {
              const el = document.getElementById('bazi-ai');
              if (el) smoothScrollToElement(el);
            }}
          >
            AI 批命 · 求测 ↓
          </button>
        )}
        {/* [MHAI] 入口结束 */}
      </header>

      {/* ---- 输入区 ---- */}
      <section className="panel controls">
        <div className="fields">
          {[['year', '年', 1900, 2100], ['month', '月', 1, 12], ['day', '日', 1, 31],
            ['hour', '时', 0, 23], ['minute', '分', 0, 59]].map(([nm, lb, mn, mx]) => (
            <label key={nm}>
              <span>{lb}</span>
              <input name={nm} type="number" min={mn} max={mx} value={form[nm]} onChange={change} />
            </label>
          ))}
          <label className="bz-gender">
            <span>性别</span>
            {['男', '女'].map(g => (
              <button key={g} type="button"
                className={form.gender === g ? 'seg on' : 'seg'}
                onClick={() => { const f = { ...form, gender: g }; setForm(f); compute(f); }}>{g}</button>
            ))}
          </label>
          <label className="bz-name-wrap">
            <span>姓名</span>
            <input className="bz-name" name="name" placeholder="可选" value={form.name} onChange={change} />
          </label>
        </div>
        {/* 存档快速操作：保存当前盘 + 下拉快速载入 */}
        <div className="bz-save-ops">
          <button type="button" className={formDirty ? 'btn ghost st-dirty' : 'btn ghost'} onClick={doSave} disabled={!bazi}
            title={formDirty ? '输入已改动但尚未排盘——保存时将自动按当前输入重新排盘' : '把当前盘保存到本机浏览器，供日后快速调出与对比'}>
            💾 存当前盘{formDirty ? '*' : ''}
          </button>
          <select className="bz-quick-load" value="" disabled={savedList.length === 0}
            onChange={(e) => {
              const rec = savedList.find(x => x.id === e.target.value);
              if (rec) loadFromSaved(rec);
            }}>
            <option value="" disabled>
              {savedList.length ? `⌄ 快速载入存档（${savedList.length} 条）` : '暂无存档'}
            </option>
            {savedList.map(r => (
              <option key={r.id} value={r.id}>
                {r.name} · {r.gender} · {r.pillars.map(p => p.gan + p.zhi).join(' ')}
              </option>
            ))}
          </select>
        </div>
        <div className="bz-city-row">
          <label className="bz-city-wrap">
            <span>真太阳时城市</span>
            <select value={city} onChange={(e) => { const c = e.target.value; setCity(c); compute(form, c); }}
              title="专业排盘标配：按出生地经度与均时差，把钟表时间换算成真太阳时再起盘；出生时间接近时辰交界处时尤其重要">
              {CITIES.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
            </select>
            <small>{city !== '不校正' && bazi && bazi.trueSolarInfo
              ? `钟表 ${bazi.clockTime} → 真太阳时 ${bazi.trueSolarInfo.text}（${bazi.trueSolarInfo.detail}）${bazi.trueSolarInfo.crossedMidnight ? ' ⚠跨午夜' : ''}`
              : '可选 · 按出生地经度校正时辰'}</small>
          </label>
        </div>
        <div className="actions">
          <button className="btn ghost" onClick={() => { const f = nowForm(); setForm(f); compute(f); }}>示例</button>
          <button className="btn primary" onClick={() => compute()}>排 盘</button>
        </div>
        <p className="bz-rule-note">年柱以立春为界 · 月柱按节气 · 23点起晚子时换日柱 · 大运阳年男顺／女逆（十神、十二长生皆以日主为基准）</p>
      </section>

      {/* ---- 已存盘命例（可折叠管理：载入 / 预览 / 编辑 / 删除 / 对比） ---- */}
      <SavedList list={savedList} setList={setSavedList} current={bazi} onLoad={loadFromSaved} />

      {error && <p className="error">⚠ {error}</p>}

      {bazi && (
        <>
          {/* ---- 基本信息 ---- */}
          <section className="summary bz-summary bz-panel" id="bazi-chart">
            <div className="sum-group">
              <h4>生辰</h4>
              <div className="sum-items">
                <div className="sum-item"><i>公历</i><b>{bazi.solarText.slice(0, 16)}</b></div>
                {bazi.trueSolarInfo && (
                  <div className="sum-item"><i>真太阳时</i><b className="gold" title={`由${bazi.input.city}经度与均时差换算：${bazi.trueSolarInfo.detail}`}>
                    {bazi.trueSolarInfo.text}{bazi.trueSolarInfo.crossedMidnight ? ' ⚠' : ''}<small style={{ color: '#8a8378' }}>（{bazi.input.city}）</small>
                  </b></div>
                )}
                <div className="sum-item"><i>农历</i><b>{bazi.lunarText}{bazi.input.gender ? ` · ${bazi.input.gender}命` : ''}{bazi.input.name ? ` · ${bazi.input.name}` : ''}</b></div>
              </div>
            </div>
            <div className="sum-group">
              <h4>命局要素</h4>
              <div className="sum-items">
                <div className="sum-item"><i>胎元</i><b title="受胎之月：月柱天干进一位、地支进三位">{bazi.taiYuan}</b></div>
                <div className="sum-item"><i>命宫</i><b title="寅起正月顺数生月、再从生月宫起子时逆数生时而得">{bazi.mingGong}</b></div>
                <div className="sum-item"><i>身宫</i><b title="与命宫互参，主后天身之所寄">{bazi.shenGong}</b></div>
                <div className="sum-item"><i>旬空</i><b className="purple" title="日柱所在旬的空亡地支">{bazi.xunKongDay}</b></div>
              </div>
            </div>
            <div className="sum-group hl">
              <h4>五行分布（8字）</h4>
              <div className="wx-bars">
                {WX_ORDER.map(w => (
                  <div key={w} className="wx-bar-row" title={`${w}：${bazi.wuxingCount[w]} 个`}>
                    <i style={{ color: WX_COLOR[w] }}>{w}</i>
                    <div className="wx-track">
                      <div className="wx-fill" style={{ width: `${(bazi.wuxingCount[w] / totalWx) * 100}%`, background: WX_COLOR[w] }} />
                    </div>
                    <em>{bazi.wuxingCount[w]}</em>
                  </div>
                ))}
                {bazi.missing.length > 0 && (
                  <p className="wx-missing">缺<b style={{ color: WX_COLOR[bazi.missing[0]] }}>{bazi.missing.join('、')}</b></p>
                )}
              </div>
            </div>
          </section>

          {/* ---- 四柱卡片 ---- */}
          <section className="panel bz-panel">
            <h4 className="sec-title">四柱 · 十神 · 藏干 · 纳音 · 十二长生</h4>
            <div className="bz-pillars">
              {bazi.pillars.map((p) => (
                <PillarCard key={p.key} p={p} isDay={p.key === 'day'}
                  active={openPillar?.key === p.key}
                  onSelect={(pp) => setOpenPillar(openPillar?.key === pp.key ? null : pp)} />
              ))}
            </div>
            {openPillar && <PillarDetail key={openPillar.key} p={openPillar} />}
            <p className="bz-tip">
              悬停任意字看释义，<b>点击任意一柱展开该柱完整详解</b>（十神落宫义·白话·藏干·纳音·长生）。
              十神以日干「{bazi.dayGan}」为基准取六亲关系；藏干为地支所藏天干（本气·中气·余气），各配十神；十二长生只论地支。
            </p>
          </section>

          {/* ---- 日主强弱 ---- */}
          <StrengthPanel strength={bazi.strength} dayGan={bazi.dayGan} />

          {/* ---- 柱际关系 ---- */}
          <RelationsPanel relations={bazi.relations} />

          {/* ---- 神煞 ---- */}
          <section className="panel bz-panel">
            <h4 className="sec-title">神煞（{bazi.shenSha.length} 颗 · 年支／日支双查）</h4>
            {bazi.shenSha.length === 0
              ? <p className="bz-tip">本命无常见神煞入盘。</p>
              : (
                <div className="shensha-grid">
                  {bazi.shenSha.map(s => (
                    <div key={s.name} className="shensha-card" title={s.positions.join(' · ')}>
                      <b>{s.name}</b>
                      <small>落{s.positions.join(' · ')}（查{s.base}）</small>
                      <p>{SHENSHA_TEXT(s.name)}</p>
                    </div>
                  ))}
                </div>
              )}
          </section>

          {/* ---- 大运流年 ---- */}
          <DayunStrip dayun={bazi.dayun} dayGan={bazi.dayGan}
            qiyunText={bazi.qiyunText} dayunDir={bazi.dayunDir} />

          {/* ---- 存档对比 ---- */}

          <footer className="note">
            规则出处：四柱立春/节气分界与晚子时换日依通行历法；十神义理出《渊海子平·论十神》《三命通会》通行说；
            十二长生表出《渊海子平》；神煞口诀（甲戊庚牛羊……、天月二德）为古法通行查法（《三命通会·论天月德》）；柱际合冲刑害表出《三命通会》；
            大运阳年男顺女逆、阴年男逆女顺，以月柱为基顺逆排布。
            起运：{bazi.qiyunText}。排盘仅供研究参考，玄学内容请理性看待。
          </footer>

          {/* [MHAI] 八字AI求测与解读（集成面板，位于命盘之下） */}
          <BaziAiChat chart={bazi} />
        </>
      )}
    </div>
  );
}

function SHENSHA_TEXT(name) {
  return SHENSHA_MEANING[name] || '';
}
